import { useState, useRef, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Video, Play, Edit2, Trash2, MoreVertical, Upload, Film, Clock, Image, Cloud, Search, Eye, CheckCircle, XCircle, ChevronDown, ChevronRight, Folder, FolderOpen, GripVertical, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getVideos, addVideo, updateVideo, deleteVideo, getCategories, getViewHistory, getProfiles, addCategory } from '@/lib/supabase';
import { getModules, addModule } from '@/lib/storage';
import { uploadSupportFile } from '@/lib/storage';
import { useNavigate } from 'react-router-dom';
import PdfViewer from '@/components/PdfViewer';
import VimeoUpload from '@/components/admin/VimeoUpload';
import { api } from '@/lib/api';
import FichaDaAula from '@/components/admin/FichaDaAula';
import { AJUDA_LIBERACAO_CONTEUDO, aggregateModuleContent, formatDurationLong, fromDateTimeLocalValue, toDateTimeLocalValue } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Tipos locais para remover any e contemplar snake/camel case vindos do backend
interface CategoryRow {
  id: string;
  name: string;
}

interface AdminVideoRow {
  id: string;
  title: string;
  description?: string;
  video_url?: string;
  videoUrl?: string;
  thumbnail?: string;
  category_id?: string;
  categoryId?: string;
  category_ids?: string[];
  duration: number;
  vimeo_id?: string;
  vimeoId?: string;
  vimeo_embed_url?: string;
  vimeoEmbedUrl?: string;
  uploadedAt?: string | Date;
  created_at?: string | Date;
  uploaded_by?: string;
  uploadedBy?: string;
  module_id?: string;
  moduleId?: string;
  order?: number;
  release_at?: string | null;
  releaseAt?: string | Date | null;
  support_files?: Array<{ id: string; url: string; filename: string; mimeType: string; size?: number }>;
  supportFiles?: Array<{ id: string; url: string; filename: string; mimeType: string; size?: number }>;
  content_type?: string;
  has_form?: boolean;
  form_url?: string | null;
  form_file?: { id: string; url: string; filename: string; mimeType: string } | null;
  formFile?: { id: string; url: string; filename: string; mimeType: string } | null;
  mentor_name?: string | null;
  mentorName?: string | null;
  mentor_bio?: string | null;
  mentorBio?: string | null;
  mentor_photo?: { id: string; url: string; filename: string; mimeType: string } | null;
  mentorPhoto?: { id: string; url: string; filename: string; mimeType: string } | null;
  contentType?: string;
}

interface ModuleWithOrder {
  id: string;
  title: string;
  parentId?: string | null;
  order?: number;
}

export default function AdminVideos() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [videos, setVideos] = useState<AdminVideoRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [modulesByCategory, setModulesByCategory] = useState<Record<string, Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }>>>({});
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isVimeoUploadOpen, setIsVimeoUploadOpen] = useState(false);
  const [isSelectTypeOpen, setIsSelectTypeOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<AdminVideoRow | null>(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [viewsMap, setViewsMap] = useState<Record<string, number>>({});
  const [previewVideo, setPreviewVideo] = useState<AdminVideoRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const [editingTitleId, setEditingTitleId] = useState<string>('');
  const [tempTitle, setTempTitle] = useState<string>('');
  const [editingCatsId, setEditingCatsId] = useState<string>('');
  const [tempCats, setTempCats] = useState<string[]>([]);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [editCategorySearch, setEditCategorySearch] = useState('');
  const [editModuleSearch, setEditModuleSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'hierarchical'>('hierarchical');
  const [listPage, setListPage] = useState(1);
  const pageSize = 12;
  
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    videoUrl: '',
    categoryId: '',
    moduleId: '',
    categoryIds: [] as string[],
    duration: 0,
    thumbnail: '',
    releaseAt: '',
    supportFiles: [] as Array<{ id: string; url: string; filename: string; mimeType: string; size?: number }>,
    hasForm: false,
    formUrl: '',
    formFile: null as { id: string; url: string; filename: string; mimeType: string } | null,
    mentorName: '',
    mentorBio: '',
    mentorPhoto: null as { id: string; url: string; filename: string; mimeType: string } | null,
  });
  const [newCatSearch, setNewCatSearch] = useState('');
  const [newModuleSearch, setNewModuleSearch] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [showNewAdvanced, setShowNewAdvanced] = useState(false);

  // Sensores para drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Função para atualizar ordem dos vídeos após drag
  const handleDragEnd = async (event: DragEndEvent, videoGroup: AdminVideoRow[]) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = videoGroup.findIndex(v => v.id === active.id);
    const newIndex = videoGroup.findIndex(v => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedVideos = arrayMove(videoGroup, oldIndex, newIndex);
    
    // Atualizar ordem no banco de dados
    try {
      const updates = reorderedVideos.map((video, index) => ({
        id: video.id,
        order: index + 1,
      }));

      // Atualizar todos os vídeos do grupo em paralelo
      await Promise.all(
        updates.map(update => 
          updateVideo(update.id, { order: update.order })
        )
      );

      // Recarregar dados para garantir sincronização
      await loadData();

      toast({
        title: 'Ordem atualizada',
        description: 'A ordem dos vídeos foi atualizada com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao atualizar ordem:', error);
      toast({
        title: 'Erro ao atualizar ordem',
        description: 'Ocorreu um erro ao salvar a nova ordem.',
        variant: 'destructive',
      });
    }
  };

  // Funções para controlar seções colapsáveis
  const toggleCategory = (categoryId: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(categoryId)) {
      newCollapsed.delete(categoryId);
    } else {
      newCollapsed.add(categoryId);
    }
    setCollapsedCategories(newCollapsed);
  };

  const toggleModule = (moduleId: string) => {
    const newCollapsed = new Set(collapsedModules);
    if (newCollapsed.has(moduleId)) {
      newCollapsed.delete(moduleId);
    } else {
      newCollapsed.add(moduleId);
    }
    setCollapsedModules(newCollapsed);
  };

  // Componente Sortable para vídeo na visualização hierárquica
  const SortableVideoRow = ({ video, onVideoClick }: { video: AdminVideoRow; onVideoClick?: () => void }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: video.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-3 p-3 border-b border-gray-100 hover:bg-gray-50 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      >
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        <input 
          type="checkbox" 
          checked={selectedIds.includes(video.id)} 
          onChange={() => toggleSelect(video.id)} 
          aria-label="Selecionar" 
          onClick={(e) => e.stopPropagation()}
        />
        <div className="flex-shrink-0">
          {video.thumbnail ? (
            <img src={video.thumbnail} alt={video.title} className="h-12 w-16 object-cover rounded" loading="lazy" />
          ) : (
            getVimeoThumbFallback(video) ? (
              <img src={getVimeoThumbFallback(video) as string} alt={video.title} className="h-12 w-16 object-cover rounded" loading="lazy" />
            ) : (
              <div className="h-12 w-16 bg-muted rounded flex items-center justify-center">
                <Video className="h-6 w-6 text-muted-foreground" />
              </div>
            )
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">
            {editingTitleId === video.id ? (
              <input
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') { await updateVideo(video.id, { title: tempTitle }); setEditingTitleId(''); await loadData(); }
                  if (e.key === 'Escape') { setEditingTitleId(''); }
                }}
                autoFocus
                className="w-full border rounded px-2 py-1"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <button className="max-w-full break-words text-left hover:underline" onClick={() => { setEditingTitleId(video.id); setTempTitle(video.title || ''); }}>{video.title}</button>
            )}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
            <Clock className="h-3 w-3" />
            {formatDurationLong(video.duration)}
            <span>•</span>
            <Eye className="h-3 w-3" />
            {viewsMap[video.id] || 0} views
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreviewVideo(video)}>
            <Eye className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => { setEditingVideo(video); setIsEditDialogOpen(true); }}>
                <Edit2 className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setPreviewVideo(video)}>
                <Play className="h-4 w-4 mr-2" />
                Visualizar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDeleteVideo(video.id)} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  // Componente Sortable para linha da tabela
  const SortableTableRow = ({ video }: { video: AdminVideoRow }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: video.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <TableRow
        ref={setNodeRef}
        style={style}
        className={isDragging ? 'cursor-grabbing' : 'cursor-grab'}
      >
        <TableCell>
          <div className="flex items-center gap-2">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <input 
              type="checkbox" 
              checked={selectedIds.includes(video.id)} 
              onChange={() => toggleSelect(video.id)} 
              aria-label="Selecionar" 
            />
          </div>
        </TableCell>
        <TableCell>
          {video.thumbnail ? (
            <img src={video.thumbnail} alt={video.title} className="h-10 w-16 object-cover rounded" loading="lazy" />
          ) : (
            getVimeoThumbFallback(video) ? (
              <img src={getVimeoThumbFallback(video) as string} alt={video.title} className="h-10 w-16 object-cover rounded" loading="lazy" />
            ) : (
              <div className="h-10 w-16 bg-muted rounded" />
            )
          )}
        </TableCell>
        <TableCell className="font-medium">
          {editingTitleId === video.id ? (
            <input
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') { await updateVideo(video.id, { title: tempTitle }); setEditingTitleId(''); await loadData(); }
                if (e.key === 'Escape') { setEditingTitleId(''); }
              }}
              autoFocus
              className="w-full border rounded px-2 py-1"
            />
          ) : (
            <button className="max-w-full break-words text-left hover:underline" onClick={() => { setEditingTitleId(video.id); setTempTitle(video.title || ''); }}>{video.title}</button>
          )}
        </TableCell>
        <TableCell className="hidden sm:table-cell">
          <div className="flex flex-wrap gap-1">
            {editingCatsId === video.id ? (
              <>
                {categories.map((c: { id: string; name: string }) => {
                  const checked = tempCats.includes(c.id);
                  return (
                    <label key={c.id} className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={checked} onChange={(e) => setTempCats(prev => e.target.checked ? Array.from(new Set([...prev, c.id])) : prev.filter(id => id !== c.id))} />{c.name}
                    </label>
                  );
                })}
              </>
            ) : (
              ((video as unknown as { category_ids?: string[]; category_id?: string }).category_ids || [video.categoryId || (video as unknown as { category_id?: string }).category_id]).filter(Boolean).slice(0,3).map((cid) => (
                <Badge key={cid} variant="secondary">
                  {getCategoryName(cid)}
                </Badge>
              ))
            )}
            {(((video as unknown as { category_ids?: string[] }).category_ids?.length) || 0) > 3 && (
              <Badge variant="outline" className="text-xs">+{((video as unknown as { category_ids?: string[] }).category_ids!.length) - 3}</Badge>
            )}
          </div>
          {editingCatsId === video.id ? (
            <div className="mt-1 flex gap-2">
              <Button size="sm" onClick={async () => { const next = tempCats; await updateVideo(video.id, { category_id: next[0], category_ids: next }); setEditingCatsId(''); await loadData(); }}>Salvar</Button>
              <Button size="sm" variant="outline" onClick={() => setEditingCatsId('')}>Cancelar</Button>
            </div>
          ) : (
            <button className="text-xs underline mt-1" onClick={() => { const current = ((video as unknown as { category_ids?: string[] }).category_ids) || (video.categoryId ? [video.categoryId] : []); setEditingCatsId(video.id); setTempCats(current); }}>editar</button>
          )}
        </TableCell>
        <TableCell className="hidden md:table-cell">{formatDurationLong(video.duration)}</TableCell>
        <TableCell className="hidden lg:table-cell">{getUploaderName(video)}</TableCell>
        <TableCell className="hidden lg:table-cell">{viewsMap[video.id] || 0}</TableCell>
        <TableCell className="hidden md:table-cell">
          {isProcessing(video) ? (
            <Badge variant="outline" className="text-xs">Processando</Badge>
          ) : isScheduled(video) ? (
            <Badge variant="outline" className="text-xs">Programado</Badge>
          ) : video.vimeoId ? (
            <Badge className="bg-primary/10 text-primary">Vimeo</Badge>
          ) : (
            <Badge variant="outline">{(video.content_type || video.contentType) === 'file' ? 'Arquivo' : 'URL'}</Badge>
          )}
        </TableCell>
        <TableCell className="hidden lg:table-cell text-muted-foreground">
          {safeFormatDate(video.uploadedAt || video.created_at)}
        </TableCell>
        <TableCell className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setPreviewVideo(video)}
              >
                <Play className="mr-2 h-4 w-4" />
                Pré-visualizar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setEditingVideo(video);
                  setIsEditDialogOpen(true);
                }}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  const alsoVimeo = window.confirm('Apagar também no Vimeo?');
                  const vId: string | undefined = (video as AdminVideoRow).vimeo_id || (video as AdminVideoRow).vimeoId;
                  if (alsoVimeo && vId) {
                    try {
                      await api.vimeoDelete(vId);
                    } catch (e) {
                      console.error('Falha ao apagar no Vimeo', e);
                    }
                  }
                  handleDeleteVideo(video.id);
                }}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    );
  };

  // Função para renderizar vídeo individual (versão não-sortable para compatibilidade)
  const renderVideoRow = (video: AdminVideoRow) => (
    <div key={video.id} className="flex items-center gap-3 p-3 border-b border-gray-100 hover:bg-gray-50">
      <input 
        type="checkbox" 
        checked={selectedIds.includes(video.id)} 
        onChange={() => toggleSelect(video.id)} 
        aria-label="Selecionar" 
      />
      <div className="flex-shrink-0">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} className="h-12 w-16 object-cover rounded" loading="lazy" />
        ) : (
          getVimeoThumbFallback(video) ? (
            <img src={getVimeoThumbFallback(video) as string} alt={video.title} className="h-12 w-16 object-cover rounded" loading="lazy" />
          ) : (
            <div className="h-12 w-16 bg-muted rounded flex items-center justify-center">
              <Video className="h-6 w-6 text-muted-foreground" />
            </div>
          )
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">
          {editingTitleId === video.id ? (
            <input
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') { await updateVideo(video.id, { title: tempTitle }); setEditingTitleId(''); await loadData(); }
                if (e.key === 'Escape') { setEditingTitleId(''); }
              }}
              autoFocus
              className="w-full border rounded px-2 py-1"
            />
          ) : (
            <button className="max-w-full break-words text-left hover:underline" onClick={() => { setEditingTitleId(video.id); setTempTitle(video.title || ''); }}>{video.title}</button>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
          <Clock className="h-3 w-3" />
          {formatDurationLong(video.duration)}
          <span>•</span>
          <Eye className="h-3 w-3" />
          {viewsMap[video.id] || 0} views
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setPreviewVideo(video)}>
          <Eye className="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => { setEditingVideo(video); setIsEditDialogOpen(true); }}>
              <Edit2 className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setPreviewVideo(video)}>
              <Play className="h-4 w-4 mr-2" />
              Visualizar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDeleteVideo(video.id)} className="text-red-600">
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  // Função para renderizar estrutura hierárquica
  const renderHierarchicalView = () => {
    const filteredVideos = videos.filter(v => 
      (filterCategory && filterCategory !== 'all' ? (v.categoryId || v.category_id) === filterCategory : true) &&
      (search ? (v.title || '').toLowerCase().includes(search.toLowerCase()) : true)
    );

    // Função auxiliar para ordenar vídeos por ordem
    const sortVideosByOrder = (videosList: AdminVideoRow[]) => {
      return [...videosList].sort((a, b) => {
        const orderA = a.order ?? (a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0);
        const orderB = b.order ?? (b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0);
        return orderA - orderB;
      });
    };

    // Função recursiva para renderizar módulos e seus filhos
    const renderModuleRecursive = (
      module: { id: string; title: string; parentId?: string | null },
      level: number,
      categoryVideos: AdminVideoRow[],
      allModules: Array<{ id: string; title: string; parentId?: string | null }>
    ) => {
      const moduleVideos = sortVideosByOrder(
        categoryVideos.filter(v => (v.moduleId || v.module_id) === module.id)
      );
      const childModules = allModules
        .filter(m => m.parentId === module.id)
        .sort((a, b) => {
          const aOrder = (a as ModuleWithOrder).order || 0;
          const bOrder = (b as ModuleWithOrder).order || 0;
          return Number(aOrder) - Number(bOrder) || String(a.title).localeCompare(String(b.title));
        });

      // Renderizar módulo mesmo sem vídeos para manter consistência com a página de Categorias & Módulos
      const isModuleCollapsed = collapsedModules.has(module.id);
      const indentStyle = { marginLeft: `${level * 2}rem` };

      return (
        <div key={module.id} style={indentStyle} className={level > 0 ? 'mt-2' : ''}>
          {/* Cabeçalho do Módulo */}
          <div 
            className={`flex items-center gap-3 p-3 border-b cursor-pointer hover:bg-gray-50 ${level > 0 ? 'border-l-2 border-gray-200 pl-4' : 'bg-white'}`}
            onClick={() => toggleModule(module.id)}
          >
            {isModuleCollapsed ? (
              <ChevronRight className="h-4 w-4 text-muted-foreground" style={{ marginLeft: level > 0 ? '0.5rem' : '1rem' }} />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" style={{ marginLeft: level > 0 ? '0.5rem' : '1rem' }} />
            )}
            <Folder className={`h-4 w-4 ${level === 0 ? 'text-green-600' : level === 1 ? 'text-orange-600' : 'text-purple-600'}`} />
            <div className="flex-1">
              <h4 className={`font-medium ${level > 0 ? 'text-sm' : ''}`}>{module.title}</h4>
              <p className="text-xs text-muted-foreground">
                {(() => {
                  const total = aggregateModuleContent(module.id, allModules, categoryVideos);
                  return `${total.videos} vídeos • ${formatDurationLong(total.duration)}`;
                })()}
              </p>
            </div>
          </div>

          {/* Conteúdo do Módulo */}
          {!isModuleCollapsed && (
            <div>
              {/* Vídeos do módulo */}
              {moduleVideos.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(e) => handleDragEnd(e, moduleVideos)}
                >
                  <SortableContext
                    items={moduleVideos.map(v => v.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div>
                      {moduleVideos.map(video => (
                        <SortableVideoRow key={video.id} video={video} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : childModules.length === 0 && (
                // Mensagem quando o módulo não tem vídeos nem submódulos
                <div className="p-4 text-center text-muted-foreground text-sm border-b">
                  Este módulo ainda não possui vídeos. Adicione vídeos através do formulário de criação.
                </div>
              )}

              {/* Submódulos recursivos */}
              {childModules.map(child => renderModuleRecursive(child, level + 1, categoryVideos, allModules))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {categories.map(category => {
          const categoryVideos = filteredVideos.filter(v => {
            const ids = v.category_ids || [v.categoryId || v.category_id].filter(Boolean);
            return ids.includes(category.id);
          });
          
          const mods = modulesByCategory[category.id] || [];
          const roots = mods.filter(m => !m.parentId)
            .sort((a, b) => (Number((a as ModuleWithOrder).order || 0) - Number((b as ModuleWithOrder).order || 0)) || String(a.title).localeCompare(String(b.title)));

          // Exibir categoria se tiver vídeos OU módulos (para manter consistência)
          if (categoryVideos.length === 0 && roots.length === 0) return null;

          const isCategoryCollapsed = collapsedCategories.has(category.id);

          return (
            <div key={category.id} className="border rounded-lg">
              {/* Cabeçalho da Categoria */}
              <div 
                className="flex items-center gap-3 p-4 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
                onClick={() => toggleCategory(category.id)}
              >
                {isCategoryCollapsed ? (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )}
                <FolderOpen className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{category.name}</h3>
                  <p className="text-sm text-muted-foreground">{categoryVideos.length} vídeos</p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDurationLong(categoryVideos.reduce((acc, v) => acc + v.duration, 0))} de conteúdo
                </div>
              </div>

              {/* Conteúdo da Categoria */}
              {!isCategoryCollapsed && (
                <div className="divide-y">
                  {roots.length > 0 ? (
                    roots.map(root => renderModuleRecursive(root, 0, categoryVideos, mods))
                  ) : (
                    /* Vídeos sem módulo específico */
                    (() => {
                      const videosWithoutModule = sortVideosByOrder(categoryVideos.filter(v => !v.moduleId && !v.module_id));
                      if (videosWithoutModule.length === 0) return null;
                      return (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(e) => handleDragEnd(e, videosWithoutModule)}
                        >
                          <SortableContext
                            items={videosWithoutModule.map(v => v.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {videosWithoutModule.map(video => (
                              <SortableVideoRow key={video.id} video={video} />
                            ))}
                          </SortableContext>
                        </DndContext>
                      );
                    })()
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setListPage(1);
  }, [search, filterCategory]);

  // Após carregar a lista, tentar preencher duração/thumbnail de vídeos do Vimeo ainda sem dados
  useEffect(() => {
    const fillMissingVimeoData = async () => {
      const pending = videos.filter(v => (v.duration || 0) === 0 || !v.thumbnail);
      for (const v of pending) {
        // extrair possível vimeoId
        const fromEmbed = (v?.vimeo_embed_url || v?.vimeoEmbedUrl)?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
        const fromUrl = (v?.video_url || v?.videoUrl)?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
        const vimeoId: string | undefined = (v as unknown as { vimeo_id?: string; vimeoId?: string }).vimeo_id || (v as unknown as { vimeo_id?: string; vimeoId?: string }).vimeoId || fromEmbed || fromUrl;
        if (!vimeoId) continue;
        try {
          const data = await api.getVimeoDetails(String(vimeoId)) as { duration?: number; thumbnail?: string | null; embedUrl?: string | null };
          const next: Partial<AdminVideoRow> & { vimeo_embed_url?: string } = {};
          if ((data?.duration ?? 0) > 0) next.duration = Math.floor(data.duration);
          if (data?.thumbnail && !v.thumbnail) next.thumbnail = String(data.thumbnail);
          if (data?.embedUrl) next.vimeo_embed_url = String(data.embedUrl);
          if (Object.keys(next).length > 0) {
            try { await updateVideo(v.id, next as unknown as AdminVideoRow); } catch {/* ignore */}
          }
        } catch {/* ignore */}
      }
    };
    if (videos.length > 0) fillMissingVimeoData();
  }, [videos]);

  const loadData = async () => {
    setLoading(true);
    const [videosData, categoriesData, viewHistory, profiles] = await Promise.all([
      getVideos(),
      getCategories(),
      getViewHistory(),
      getProfiles(),
    ]);
    // Ordenar vídeos por ordem (já vem ordenado do backend, mas garantimos aqui também)
    const sortedVideos = [...videosData].sort((a, b) => {
      const orderA = (a as AdminVideoRow).order ?? (a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0);
      const orderB = (b as AdminVideoRow).order ?? (b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0);
      return orderA - orderB;
    });
    setVideos(sortedVideos);
    setCategories(categoriesData);
    // Carregar módulos por categoria
    const modMap: Record<string, Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }>> = {};
    for (const c of categoriesData) {
      const list = await getModules(c.id);
      modMap[c.id] = list.map(m => ({ id: m.id, title: m.title, parentId: m.parentId ?? null, categoryId: m.categoryId }));
    }
    setModulesByCategory(modMap);
    const pmap: Record<string, string> = {};
    for (const p of profiles as Array<{ id?: string; name?: string; email?: string }> ) {
      if (p && p.id) pmap[p.id] = (p.name || p.email || '');
    }
    setProfileMap(pmap);
    // construir mapa de views por videoId
    const temp: Record<string, number> = {};
    for (const vh of viewHistory) {
      temp[vh.videoId] = (temp[vh.videoId] || 0) + 1;
    }
    setViewsMap(temp);
    setLoading(false);
  };

  const handleAddVideo = async () => {
    if (!newVideo.title || !newVideo.videoUrl || (newVideo.categoryIds || []).length === 0) {
      toast({
        title: "Erro ao adicionar vídeo",
        description: "Preencha título, URL e selecione ao menos uma categoria.",
        variant: "destructive",
      });
      return;
    }

    try {
      const allCats = Array.from(new Set([...(newVideo.categoryIds || [])]));
      const mainId = allCats[0];
      await addVideo({
        title: newVideo.title,
        description: newVideo.description,
        video_url: newVideo.videoUrl,
        thumbnail: newVideo.thumbnail || undefined,
        category_id: mainId,
        category_ids: allCats,
        module_id: newVideo.moduleId || undefined,
        duration: newVideo.duration,
        uploaded_by: 'admin',
        release_at: fromDateTimeLocalValue(newVideo.releaseAt),
        support_files: newVideo.supportFiles,
        has_form: newVideo.hasForm,
        form_url: newVideo.hasForm ? (newVideo.formUrl || null) : null,
        form_file: newVideo.hasForm ? newVideo.formFile : null,
        content_type: newVideo.videoUrl.includes('/api/files/') ? 'file' : 'video',
        mentor_name: newVideo.mentorName || null,
        mentor_bio: newVideo.mentorBio || null,
        mentor_photo: newVideo.mentorPhoto || null,
      });

      await loadData();
      setIsAddDialogOpen(false);
      setNewVideo({
        title: '',
        description: '',
        videoUrl: '',
        categoryId: '',
        categoryIds: [],
        moduleId: '',
        duration: 0,
        thumbnail: '',
        releaseAt: '',
        supportFiles: [],
        hasForm: false,
        formUrl: '',
        formFile: null,
        mentorName: '',
        mentorBio: '',
        mentorPhoto: null,
      });

      toast({
        title: "Vídeo adicionado",
        description: "O vídeo foi adicionado com sucesso.",
      });
    } catch (error) {
      toast({
        title: "Erro ao adicionar vídeo",
        description: "Ocorreu um erro ao cadastrar o vídeo.",
        variant: "destructive",
      });
    }
  };
  
  const handleVimeoUploadComplete = async () => {
    await loadData();
    setIsVimeoUploadOpen(false);
    toast({
      title: "Vídeo do Vimeo adicionado",
      description: "O vídeo foi enviado para o Vimeo e adicionado à plataforma.",
    });
  };

  const handleUpdateVideo = async () => {
    if (!editingVideo) return;

    try {
      const mainCat = editingVideo.category_id || editingVideo.categoryId;
      if (!mainCat) {
        toast({ title: 'Selecione a categoria principal', variant: 'destructive' });
        return;
      }

      // Converter module_id vazio para null
      const moduleId = editingVideo.module_id || editingVideo.moduleId;
      const finalModuleId = (moduleId && moduleId.trim() !== '') ? moduleId : null;

      // Validação: se module_id estiver definido, verificar se a categoria do módulo corresponde
      if (finalModuleId) {
        let moduleCategoryId = '';
        for (const catId in modulesByCategory) {
          const module = modulesByCategory[catId].find(m => m.id === finalModuleId);
          if (module) {
            moduleCategoryId = module.categoryId || catId;
            break;
          }
        }
        
        // Se a categoria do módulo não corresponder à categoria do vídeo, atualizar automaticamente
        if (moduleCategoryId && moduleCategoryId !== mainCat) {
          const currentCategoryIds = editingVideo.category_ids || [mainCat];
          const newCategoryIds = Array.from(new Set([moduleCategoryId, ...currentCategoryIds]));
          editingVideo.category_id = moduleCategoryId;
          editingVideo.categoryId = moduleCategoryId;
          editingVideo.category_ids = newCategoryIds;
        }
      }

      await updateVideo(editingVideo.id, {
        title: editingVideo.title,
        description: editingVideo.description,
        video_url: editingVideo.video_url || editingVideo.videoUrl,
        thumbnail: editingVideo.thumbnail,
        category_id: editingVideo.category_id || editingVideo.categoryId,
        category_ids: editingVideo.category_ids,
        module_id: finalModuleId,
        duration: editingVideo.duration,
        release_at: editingVideo.release_at || (editingVideo.releaseAt ? new Date(editingVideo.releaseAt).toISOString() : null),
        support_files: editingVideo.support_files || editingVideo.supportFiles || [],
        has_form: !!editingVideo.has_form,
        form_url: editingVideo.has_form ? (editingVideo.form_url || null) : null,
        form_file: editingVideo.has_form ? (editingVideo.form_file || null) : null,
        content_type: editingVideo.content_type || editingVideo.contentType || ((editingVideo.video_url || editingVideo.videoUrl || '').includes('/api/files/') ? 'file' : 'video'),
        mentor_name: editingVideo.mentorName ?? editingVideo.mentor_name ?? null,
        mentor_bio: editingVideo.mentorBio ?? editingVideo.mentor_bio ?? null,
        mentor_photo: editingVideo.mentorPhoto ?? editingVideo.mentor_photo ?? null,
      });
      
      await loadData();
      setIsEditDialogOpen(false);
      setEditingVideo(null);

      toast({
        title: "Vídeo atualizado",
        description: "As informações do vídeo foram atualizadas.",
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar vídeo",
        description: "Ocorreu um erro ao atualizar o vídeo.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este vídeo?')) return;
    const backup = videos.slice();
    try {
      await deleteVideo(videoId);
      await loadData();
      let undo = true;
      toast({
        title: 'Vídeo excluído',
        description: 'Clique para desfazer',
      });
      setTimeout(async () => {
        if (!undo) return;
      }, 8000);
      // Simplificação: mostrar prompt de desfazer via confirm
      setTimeout(async () => {
        if (window.confirm('Desfazer exclusão do vídeo?')) {
          // Restauração básica em memória (somente visual até recarregar)
          setVideos(backup);
          toast({ title: 'Ação desfeita' });
          undo = false;
        }
      }, 100);
    } catch (error) {
      toast({
        title: 'Erro ao excluir vídeo',
        description: 'Ocorreu um erro ao remover o vídeo.',
        variant: 'destructive',
      });
    }
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        if (isEdit && editingVideo) {
          setEditingVideo({ ...editingVideo, thumbnail: base64String });
        } else {
          setNewVideo({ ...newVideo, thumbnail: base64String });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const safeFormatDate = (value: Date | string | number | null | undefined) => {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR');
  };

  const computeVimeoEmbed = (video: AdminVideoRow | null): string | null => {
    if (!video) return null;
    if (video.vimeo_embed_url || video.vimeoEmbedUrl) return String(video.vimeo_embed_url || video.vimeoEmbedUrl);
    if (video.vimeo_id) return `https://player.vimeo.com/video/${video.vimeo_id}`;
    const url: string | undefined = video.video_url || video.videoUrl;
    const match = url?.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (match?.[1]) return `https://player.vimeo.com/video/${match[1]}`;
    return null;
  };

  const getUploaderName = (v: AdminVideoRow): string => {
    const raw = (v.uploaded_by || v.uploadedBy || '').trim();
    if (!raw) return 'admin';
    // Se for UUID conhecido, tentar mapear para o nome do perfil
    const uuidLike = /^[0-9a-fA-F-]{30,}$/;
    if (uuidLike.test(raw) && profileMap[raw]) return profileMap[raw];
    return raw;
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Sem categoria';
  };

  const getVimeoThumbFallback = (v: AdminVideoRow): string | null => {
    // Tentar extrair de vários campos (embed/url/id)
    const fromEmbed = (v?.vimeo_embed_url || v?.vimeoEmbedUrl)?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
    const fromUrl = (v?.video_url || v?.videoUrl)?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
    const id = v?.vimeoId || v?.vimeo_id || fromEmbed || fromUrl;
    return id ? `https://vumbnail.com/${id}.jpg` : null;
  };

  const isProcessing = (v: AdminVideoRow) => {
    return (v.duration || 0) === 0 || !v.thumbnail;
  };

  const isScheduled = (v: AdminVideoRow) => {
    const raw = v.release_at || v.releaseAt;
    if (!raw) return false;
    const date = new Date(raw);
    return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const applyBulkMove = async () => {
    if (!bulkCategoryId || selectedIds.length === 0) return;
    for (const id of selectedIds) {
      const v = videos.find(x => x.id === id);
      if (!v) continue;
      const current = ((v as unknown as { category_ids?: string[] }).category_ids) || (v.categoryId ? [v.categoryId] : []);
      const next = Array.from(new Set([bulkCategoryId, ...current]));
      await updateVideo(id, { category_id: next[0], category_ids: next });
    }
    setSelectedIds([]);
    setBulkCategoryId('');
    await loadData();
    toast({ title: 'Vídeos atualizados', description: 'Categorias aplicadas' });
  };

  const handleFixModuleCategorySync = async () => {
    if (!confirm('Isso irá corrigir a sincronização entre módulos e categorias de todos os vídeos. Deseja continuar?')) {
      return;
    }
    
    try {
      const data = await api.fixModuleCategorySync();
      await loadData();
      
      toast({
        title: 'Correção concluída',
        description: data.message || `${data.videosFixed || 0} vídeo(s) corrigido(s).`,
      });
    } catch (error) {
      toast({
        title: 'Erro ao corrigir',
        description: 'Ocorreu um erro ao executar a correção de sincronização.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-poppins font-bold">Gerenciar Vídeos</h1>
            <p className="text-muted-foreground">
              Adicione e gerencie os vídeos de treinamento da plataforma
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleFixModuleCategorySync}
              variant="outline"
              className="gap-2"
              title="Corrigir sincronização entre módulos e categorias"
            >
              <RefreshCw className="h-4 w-4" />
              Corrigir Sincronização
            </Button>
            <Button 
              onClick={() => setIsSelectTypeOpen(true)}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              Enviar novo material
            </Button>
          </div>
        </div>

        {/* Filtros e Busca */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative md:w-1/2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por título..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="md:w-1/3">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Total de Vídeos</CardTitle>
                <Film className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{videos.length}</div>
              <p className="text-xs text-muted-foreground">vídeos disponíveis</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Categorias</CardTitle>
                <Video className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{categories.length}</div>
              <p className="text-xs text-muted-foreground">categorias ativas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Duração Total</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDurationLong(videos.reduce((acc, v) => acc + v.duration, 0))}
              </div>
              <p className="text-xs text-muted-foreground">de conteúdo</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Vídeos Vimeo</CardTitle>
                <Image className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {videos.filter(v => v.vimeo_id).length}
              </div>
              <p className="text-xs text-muted-foreground">integrados com Vimeo</p>
            </CardContent>
          </Card>
        </div>

        {/* Videos Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Lista de Vídeos</CardTitle>
                <CardDescription>
                  Todos os vídeos disponíveis na plataforma
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'hierarchical' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('hierarchical')}
                  className="flex items-center gap-2"
                >
                  <Folder className="h-4 w-4" />
                  Hierárquico
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="flex items-center gap-2"
                >
                  <Video className="h-4 w-4" />
                  Lista
                </Button>
              </div>
            </div>
            {selectedIds.length > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm">Selecionados: {selectedIds.length}</span>
                <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
                  <SelectTrigger className="w-[240px]"><SelectValue placeholder="Mover para categoria" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={applyBulkMove} disabled={!bulkCategoryId}>Aplicar</Button>
                <Button size="sm" variant="outline" onClick={() => setSelectedIds([])}>Limpar</Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {viewMode === 'hierarchical' ? (
              renderHierarchicalView()
            ) : (
              (() => {
                const filteredVideosList = videos
                  .filter(v => (filterCategory && filterCategory !== 'all' ? (v.categoryId || v.category_id) === filterCategory : true))
                  .filter(v => (search ? (v.title || '').toLowerCase().includes(search.toLowerCase()) : true))
                  .sort((a, b) => {
                    const orderA = a.order ?? (a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0);
                    const orderB = b.order ?? (b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0);
                    return orderA - orderB;
                  });
                const totalListPages = Math.max(1, Math.ceil(filteredVideosList.length / pageSize));
                const safePage = Math.min(listPage, totalListPages);
                const pageVideos = filteredVideosList.slice((safePage - 1) * pageSize, safePage * pageSize);

                return (
                  <div className="space-y-4">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(e) => handleDragEnd(e, pageVideos)}
                    >
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              <input type="checkbox" onChange={(e) => setSelectedIds(e.target.checked ? pageVideos.map(v => v.id) : [])} checked={pageVideos.length > 0 && selectedIds.length === pageVideos.length} aria-label="Selecionar todos" />
                            </TableHead>
                            <TableHead>Thumb</TableHead>
                            <TableHead>Título</TableHead>
                            <TableHead className="hidden sm:table-cell">Categoria</TableHead>
                            <TableHead className="hidden md:table-cell">Duração</TableHead>
                            <TableHead className="hidden lg:table-cell">Enviado por</TableHead>
                            <TableHead className="hidden lg:table-cell">Views</TableHead>
                            <TableHead className="hidden md:table-cell">Status</TableHead>
                            <TableHead className="hidden lg:table-cell">Upload</TableHead>
                            <TableHead className="text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <SortableContext
                          items={pageVideos.map(v => v.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <TableBody>
                            {pageVideos.map(video => (
                              <SortableTableRow key={video.id} video={video} />
                            ))}
                          </TableBody>
                        </SortableContext>
                      </Table>
                    </DndContext>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{filteredVideosList.length} conteúdo(s) encontrados</span>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setListPage((p) => Math.max(1, p - 1))}>Anterior</Button>
                        <span>Página {safePage} de {totalListPages}</span>
                        <Button size="sm" variant="outline" disabled={safePage >= totalListPages} onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}>Próxima</Button>
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
          </CardContent>
        </Card>

        {/* Seletor de tipo de material */}
        <Dialog open={isSelectTypeOpen} onOpenChange={setIsSelectTypeOpen}>
          <DialogContent className="sm:max-w-[480px]" aria-describedby="select-type-desc">
            <DialogHeader>
              <DialogTitle>Escolha o tipo de material</DialogTitle>
              <DialogDescription id="select-type-desc">Selecione se deseja enviar um vídeo (Vimeo) ou um arquivo (PDF/JPG/PNG)</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              <Button className="h-20 gap-2" variant="outline" onClick={() => { setIsSelectTypeOpen(false); navigate('/admin/vimeo-upload'); }}>
                <Cloud className="h-5 w-5" /> Vídeo (Vimeo)
              </Button>
              <Button className="h-20 gap-2" onClick={() => { setIsSelectTypeOpen(false); navigate('/admin/material-upload'); }}>
                <Upload className="h-5 w-5" /> Arquivo (PDF/JPG/PNG)
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Video Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Adicionar Novo Vídeo</DialogTitle>
              <DialogDescription>
                Escolha como deseja adicionar o vídeo
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddDialogOpen(false);
                    setIsVimeoUploadOpen(true);
                  }}
                  className="w-full h-24 flex flex-col gap-2"
                >
                  <Cloud className="h-8 w-8 text-primary" />
                  <div className="text-center">
                    <p className="font-semibold">Upload para Vimeo</p>
                    <p className="text-xs text-muted-foreground">Faça upload direto para o Vimeo</p>
                  </div>
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">ou</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Adicionar URL de Vídeo</h3>
                  <div className="grid gap-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={newVideo.title}
                      onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                      placeholder="Ex: Introdução ao Sistema"
                    />
                  </div>
                  <FichaDaAula
                    idPrefixo="novo"
                    valor={newVideo}
                    aoMudar={(mudanca) => setNewVideo({ ...newVideo, ...mudanca } as typeof newVideo)}
                  />
                  <div className="grid gap-2">
                    <Label htmlFor="category">Categorias (múltiplas) *</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="w-full min-h-10 border rounded px-2 py-2 text-left flex items-center flex-wrap gap-1">
                          {(() => {
                            const selected = categories.filter(c => (newVideo.categoryIds || []).includes(c.id));
                            return selected.length > 0 ? (
                              selected.map(s => (
                                <span key={s.id} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                  {s.name}
                                  <button type="button" className="opacity-70 hover:opacity-100" onClick={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    setNewVideo(v => ({ ...v, categoryIds: (v.categoryIds || []).filter(id => id !== s.id) }));
                                  }}>×</button>
                                </span>
                              ))
                            ) : (
                              <span className="text-muted-foreground text-sm">Selecionar categorias...</span>
                            );
                          })()}
                          <span className="ml-auto text-muted-foreground text-sm">▼</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-80">
                        <div className="px-2 py-2">
                          <Input placeholder="Buscar categoria..." value={newCatSearch} onChange={(e) => setNewCatSearch(e.target.value)} />
                        </div>
                        <div className="max-h-72 overflow-auto">
                          {categories
                            .filter(c => (newCatSearch ? (c.name || '').toLowerCase().includes(newCatSearch.toLowerCase()) : true))
                            .map(category => {
                              const checked = (newVideo.categoryIds || []).includes(category.id);
                              return (
                                <DropdownMenuItem
                                  key={category.id}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    setNewVideo(v => ({ ...v,
                                      categoryIds: checked ? (v.categoryIds || []).filter(id => id !== category.id) : Array.from(new Set([...(v.categoryIds || []), category.id]))
                                    }));
                                  }}
                                >
                                  <input type="checkbox" readOnly checked={checked} className="mr-2" />
                                  {category.name}
                                </DropdownMenuItem>
                              );
                            })}
                          {/* Criar categoria inline */}
                          {newCatSearch && !categories.some(c => (c.name || '').toLowerCase() === newCatSearch.toLowerCase()) && (
                            <DropdownMenuItem
                              onSelect={async (e) => {
                                e.preventDefault();
                                try {
                                  setCreatingNew(true);
                                  const row = await addCategory({ name: newCatSearch, description: '' });
                                  const cats = await getCategories();
                                  setCategories(cats);
                                  const newId = (row as unknown as { id?: string })?.id || cats.find(c => c.name === newCatSearch)?.id;
                                  if (newId) setNewVideo(v => ({ ...v, categoryIds: Array.from(new Set([...(v.categoryIds || []), newId])) }));
                                  setNewCatSearch('');
                                  toast({ title: 'Categoria criada' });
                                } catch { toast({ title: 'Erro ao criar categoria', variant: 'destructive' }); }
                                finally { setCreatingNew(false); }
                              }}
                            >
                              {creatingNew ? 'Criando...' : `+ Criar categoria "${newCatSearch}"`}
                            </DropdownMenuItem>
                          )}
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate('/admin/taxonomia')}>Gerenciar categorias/módulos</Button>
                    {newVideo.categoryId && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/taxonomia?categoryId=${newVideo.categoryId}`)}>Abrir categoria atual</Button>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label>Avançado</Label>
                      <Button size="sm" variant="outline" onClick={() => setShowNewAdvanced(!showNewAdvanced)}>
                        {showNewAdvanced ? 'Ocultar' : 'Mostrar'}
                      </Button>
                    </div>
                    {showNewAdvanced && (
                      <div className="space-y-2">
                        <div>
                          <Label>Categorias (múltiplas)</Label>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="w-full min-h-10 border rounded px-2 py-2 text-left flex items-center flex-wrap gap-1">
                                {(() => {
                                  const selectedIds: string[] = Array.from(new Set([newVideo.categoryId, ...newVideo.categoryIds].filter(Boolean)));
                                  const selected = categories.filter(c => selectedIds.includes(c.id));
                                  return selected.length > 0 ? (
                                    selected.map(s => (
                                      <span key={s.id} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                        {s.name}
                                        {(s.id !== newVideo.categoryId) && (
                                          <button
                                            type="button"
                                            className="opacity-70 hover:opacity-100"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              const current = selectedIds;
                                              const next = current.filter(id => id !== s.id);
                                              const ensured = newVideo.categoryId && !next.includes(newVideo.categoryId) ? [newVideo.categoryId, ...next] : next;
                                              setNewVideo({ ...newVideo, categoryIds: ensured.filter(id => id !== newVideo.categoryId) });
                                            }}
                                          >×</button>
                                        )}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-muted-foreground text-sm">Selecionar categorias...</span>
                                  );
                                })()}
                                <span className="ml-auto text-muted-foreground text-sm">▼</span>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-80">
                              <div className="px-2 py-2">
                                <Input
                                  placeholder="Buscar categoria..."
                                  value={newCatSearch}
                                  onChange={(e) => setNewCatSearch(e.target.value)}
                                />
                              </div>
                              <div className="max-h-72 overflow-auto">
                                {categories
                                  .filter((c) => (newCatSearch ? (c.name || '').toLowerCase().includes(newCatSearch.toLowerCase()) : true))
                                  .map((category) => {
                                    const list: string[] = Array.from(new Set([newVideo.categoryId, ...newVideo.categoryIds].filter(Boolean)));
                                    const checked = list.includes(category.id);
                                    const disabled = category.id === newVideo.categoryId; // principal sempre incluída
                                    return (
                                      <DropdownMenuItem
                                        key={category.id}
                                        onSelect={(e) => {
                                          e.preventDefault();
                                          const current = list;
                                          let next: string[];
                                          if (checked) {
                                            if (disabled) return; // não remove a principal
                                            next = current.filter((id) => id !== category.id);
                                          } else {
                                            next = Array.from(new Set([...current, category.id]));
                                          }
                                          const ensured = newVideo.categoryId && !next.includes(newVideo.categoryId) ? [newVideo.categoryId, ...next] : next;
                                          setNewVideo({ ...newVideo, categoryIds: ensured.filter(id => id !== newVideo.categoryId) });
                                        }}
                                      >
                                        <input type="checkbox" readOnly checked={checked} className="mr-2" />
                                        {category.name}
                                        {disabled && <span className="ml-auto text-xs text-muted-foreground">principal</span>}
                                      </DropdownMenuItem>
                                    );
                                  })}
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <p className="text-xs text-muted-foreground mt-1">A categoria principal sempre ficará selecionada.</p>
                        </div>
                      </div>
                    )}
                  </div>
                {(newVideo.categoryIds || []).length > 0 && (
                  <div className="grid gap-2">
                    <Label htmlFor="module">Módulo/Submódulo</Label>
                    <Input
                      placeholder="Buscar módulo..."
                      value={newModuleSearch}
                      onChange={(e) => setNewModuleSearch(e.target.value)}
                    />
                    <Select
                      value={newVideo.moduleId}
                      onValueChange={(value) => setNewVideo({ ...newVideo, moduleId: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um módulo (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // União de módulos das categorias selecionadas
                          const sets: Array<{ id: string; title: string; parentId?: string | null; order?: number }> = [];
                          for (const cid of (newVideo.categoryIds || [])) {
                            for (const m of (modulesByCategory[cid] || [])) {
                              sets.push(m);
                            }
                          }
                          const all = sets.filter((m) => (newModuleSearch ? (m.title || '').toLowerCase().includes(newModuleSearch.toLowerCase()) : true));
                          const roots = all.filter(m => !m.parentId)
                            .sort((a, b) => (Number((a as ModuleWithOrder).order || 0) - Number((b as ModuleWithOrder).order || 0)) || String(a.title).localeCompare(String(b.title)));

                          // Função recursiva para renderizar módulos
                          const renderModuleOption = (module: { id: string; title: string; parentId?: string | null }, level: number): JSX.Element[] => {
                            const children = all
                              .filter(m => m.parentId === module.id)
                              .sort((a, b) => (Number((a as ModuleWithOrder).order || 0) - Number((b as ModuleWithOrder).order || 0)) || String(a.title).localeCompare(String(b.title)));
                            
                            const indent = '↳ '.repeat(level);
                            const padding = level * 1;
                            
                            const result: JSX.Element[] = [
                              <SelectItem key={module.id} value={module.id}>
                                <span style={{ paddingLeft: `${padding * 0.5}rem` }}>{indent}{module.title}</span>
                              </SelectItem>
                            ];

                            children.forEach(child => {
                              result.push(...renderModuleOption(child, level + 1));
                            });

                            return result;
                          };

                          return (
                            <div className="max-h-64 overflow-auto">
                              {roots.map(root => (
                                <div key={root.id}>
                                  <div className="px-2 py-1 text-xs text-muted-foreground uppercase">{root.title}</div>
                                  {renderModuleOption(root, 0)}
                                </div>
                              ))}
                              {/* Criar módulo inline */}
                              {newModuleSearch && !all.some(m => (m.title || '').toLowerCase() === newModuleSearch.toLowerCase()) && (
                                <div className="px-2 py-1">
                                  <Button size="sm" onClick={async () => {
                                    try {
                                      setCreatingNew(true);
                                      for (const cid of (newVideo.categoryIds || [])) {
                                        await addModule({ categoryId: cid, parentId: null, title: newModuleSearch, order: 999 });
                                      }
                                      // recarrega módulos
                                      const modMap: Record<string, Array<{ id: string; title: string; parentId?: string | null }>> = {};
                                      for (const c of categories) {
                                        const list = await getModules(c.id);
                                        modMap[c.id] = list.map(m => ({ id: m.id, title: m.title, parentId: m.parentId ?? null }));
                                      }
                                      setModulesByCategory(modMap);
                                      setNewModuleSearch('');
                                      toast({ title: 'Módulo criado' });
                                    } catch { toast({ title: 'Erro ao criar módulo', variant: 'destructive' }); }
                                    finally { setCreatingNew(false); }
                                  }}>{creatingNew ? 'Criando...' : `+ Criar módulo "${newModuleSearch}"`}</Button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                  <div className="grid gap-2">
                    <Label htmlFor="url">URL do Vídeo *</Label>
                    <Input
                      id="url"
                      type="url"
                      value={newVideo.videoUrl}
                      onChange={(e) => setNewVideo({ ...newVideo, videoUrl: e.target.value })}
                      placeholder="https://exemplo.com/video.mp4"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="duration">Duração (segundos) *</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={newVideo.duration}
                      onChange={(e) => setNewVideo({ ...newVideo, duration: parseInt(e.target.value) || 0 })}
                      placeholder="120"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="release-at">Liberação programada</Label>
                    <Input
                      id="release-at"
                      type="datetime-local"
                      value={newVideo.releaseAt}
                      onChange={(e) => setNewVideo({ ...newVideo, releaseAt: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">{AJUDA_LIBERACAO_CONTEUDO}</p>
                  </div>
                  <div className="grid gap-2">
                    <Label>Thumbnail</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleThumbnailUpload(e)}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Escolher Imagem
                      </Button>
                    </div>
                    {newVideo.thumbnail && (
                      <img
                        src={newVideo.thumbnail}
                        alt="Thumbnail preview"
                        className="mt-2 h-24 w-auto rounded-md object-cover"
                      />
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">ou</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Enviar arquivo como aula</h3>
                    <Input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,text/plain,text/csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const res = await uploadSupportFile(f);
                          setNewVideo({ ...newVideo, videoUrl: res.url, thumbnail: f.type.startsWith('image/') ? res.url : newVideo.thumbnail, duration: 0 });
                        } catch (err) {
                          toast({ title: 'Falha ao enviar arquivo', variant: 'destructive' });
                        }
                      }}
                    />
                    <p className="text-xs text-muted-foreground">O arquivo ocupará a mesma estrutura dos vídeos. Para imagens, usaremos a própria imagem como thumbnail.</p>
                  </div>
                  <div className="space-y-2 rounded-md border p-3">
                    <Label className="text-sm font-medium">Este conteúdo possui formulário?</Label>
                    <p className="text-xs text-muted-foreground">
                      Marcando “Sim”, o aluno vê a chamada para a atividade ao terminar a aula.
                      É obrigatório informar um link ou anexar o formulário em Word ou PDF.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={newVideo.hasForm ? 'default' : 'outline'}
                        onClick={() => setNewVideo({ ...newVideo, hasForm: true })}
                      >Sim</Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={!newVideo.hasForm ? 'default' : 'outline'}
                        onClick={() => setNewVideo({ ...newVideo, hasForm: false, formUrl: '', formFile: null })}
                      >Não</Button>
                    </div>
                    {newVideo.hasForm && (
                      <div className="space-y-2 pt-1">
                        <Input
                          type="url"
                          inputMode="url"
                          placeholder="Link do formulário (Google Forms, Microsoft Forms...)"
                          value={newVideo.formUrl || ''}
                          onChange={(e) => setNewVideo({ ...newVideo, formUrl: e.target.value })}
                        />
                        <div className="text-xs text-muted-foreground">ou anexe o arquivo:</div>
                        <Input
                          type="file"
                          accept="application/pdf,.doc,.docx"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            try {
                              const res = await uploadSupportFile(f);
                              setNewVideo({ ...newVideo, formFile: res });
                              toast({ title: 'Formulário anexado' });
                            } catch {
                              toast({ title: 'Falha ao enviar o formulário', variant: 'destructive' });
                            }
                          }}
                        />
                        {newVideo.formFile && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="truncate">{newVideo.formFile.filename}</span>
                            <button type="button" className="text-destructive"
                                    onClick={() => setNewVideo({ ...newVideo, formFile: null })}>remover</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Materiais de apoio desta aula</h3>
                    <Input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,text/plain,text/csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      multiple
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        try {
                          const uploaded = [];
                          for (const f of files) {
                            uploaded.push(await uploadSupportFile(f));
                          }
                          setNewVideo({ ...newVideo, supportFiles: [...newVideo.supportFiles, ...uploaded] });
                        } catch (err) {
                          toast({ title: 'Falha ao enviar material de apoio', variant: 'destructive' });
                        } finally {
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    {newVideo.supportFiles.length > 0 && (
                      <div className="space-y-1">
                        {newVideo.supportFiles.map((f) => (
                          <div key={f.id} className="flex items-center justify-between rounded border px-2 py-1 text-xs">
                            <span className="truncate">{f.filename}</span>
                            <button type="button" className="text-destructive" onClick={() => setNewVideo({ ...newVideo, supportFiles: newVideo.supportFiles.filter((x) => x.id !== f.id) })}>remover</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAddVideo}
                disabled={!newVideo.title || !newVideo.videoUrl || (newVideo.categoryIds || []).length === 0}
              >
                Adicionar Vídeo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Vimeo Upload Dialog */}
        <Dialog open={isVimeoUploadOpen} onOpenChange={setIsVimeoUploadOpen}>
          <DialogContent className="max-w-3xl h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload de Vídeo para o Vimeo</DialogTitle>
              <DialogDescription>
                Faça upload do seu vídeo diretamente para o Vimeo
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <VimeoUpload />
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsVimeoUploadOpen(false);
                  handleVimeoUploadComplete();
                }}
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Video Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Editar Vídeo</DialogTitle>
              <DialogDescription>
                Atualize as informações do vídeo
              </DialogDescription>
            </DialogHeader>
            {editingVideo && (
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="edit-title">Título</Label>
                  <Input
                    id="edit-title"
                    value={editingVideo.title}
                    onChange={(e) => setEditingVideo({ ...editingVideo, title: e.target.value })}
                  />
                </div>
                {/* A listagem vem crua do servidor, em snake_case; a ficha trabalha
                    em camelCase e é assim que handleUpdateVideo lê de volta. */}
                <FichaDaAula
                  idPrefixo="editar"
                  valor={{
                    mentorName: editingVideo.mentorName ?? editingVideo.mentor_name ?? '',
                    mentorBio: editingVideo.mentorBio ?? editingVideo.mentor_bio ?? '',
                    mentorPhoto: editingVideo.mentorPhoto ?? editingVideo.mentor_photo ?? null,
                    description: editingVideo.description,
                  }}
                  aoMudar={(mudanca) => setEditingVideo({ ...editingVideo, ...mudanca })}
                />
                <div className="grid gap-2">
                  <Label>Categoria Principal</Label>
                  <Select
                    value={(editingVideo.category_id || editingVideo.categoryId || '') as string}
                    onValueChange={(value) => setEditingVideo({ ...editingVideo, category_id: value, categoryId: value, module_id: '', moduleId: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a categoria principal" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1">
                        <Input
                          placeholder="Buscar categoria..."
                          value={editCategorySearch}
                          onChange={(e) => setEditCategorySearch(e.target.value)}
                        />
                      </div>
                      {categories
                        .filter((c) => (editCategorySearch ? (c.name || '').toLowerCase().includes(editCategorySearch.toLowerCase()) : true))
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {!((editingVideo.category_id || editingVideo.categoryId)) && (
                    <p className="text-xs text-destructive">Categoria principal é obrigatória</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label>Avançado</Label>
                    <Button size="sm" variant="outline" onClick={() => setShowAdvanced(!showAdvanced)}>
                      {showAdvanced ? 'Ocultar' : 'Mostrar'}
                    </Button>
                  </div>
                  {showAdvanced && (
                    <div className="space-y-2">
                      <div>
                        <Label>Categorias (múltiplas)</Label>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="w-full min-h-10 border rounded px-2 py-2 text-left flex items-center flex-wrap gap-1">
                              {(() => {
                                const selectedIds: string[] = (editingVideo.category_ids || (editingVideo.categoryId ? [editingVideo.categoryId] : [])) as unknown as string[];
                                const selected = categories.filter(c => selectedIds.includes(c.id));
                                return selected.length > 0 ? (
                                  selected.map(s => (
                                    <span key={s.id} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground text-xs px-2 py-1 rounded">
                                      {s.name}
                                      {(s.id !== (editingVideo.category_id || editingVideo.categoryId)) && (
                                        <button
                                          type="button"
                                          className="opacity-70 hover:opacity-100"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const main = (editingVideo.category_id || editingVideo.categoryId) as string | undefined;
                                            const current = selectedIds;
                                            const next = current.filter(id => id !== s.id);
                                            // garantir principal presente
                                            const ensured = main && !next.includes(main) ? [main, ...next] : next;
                                            setEditingVideo({ ...editingVideo, category_ids: ensured, categoryId: main || ensured[0] || '' });
                                          }}
                                        >×</button>
                                      )}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-muted-foreground text-sm">Selecionar categorias...</span>
                                );
                              })()}
                              <span className="ml-auto text-muted-foreground text-sm">▼</span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-80">
                            <div className="px-2 py-2">
                              <Input
                                placeholder="Buscar categoria..."
                                value={editCategorySearch}
                                onChange={(e) => setEditCategorySearch(e.target.value)}
                              />
                            </div>
                            <div className="max-h-72 overflow-auto">
                              {categories
                                .filter((c) => (editCategorySearch ? (c.name || '').toLowerCase().includes(editCategorySearch.toLowerCase()) : true))
                                .map((category) => {
                                  const list: string[] = (editingVideo.category_ids || (editingVideo.categoryId ? [editingVideo.categoryId] : [])) as unknown as string[];
                                  const checked = list.includes(category.id);
                                  const main = (editingVideo.category_id || editingVideo.categoryId) as string | undefined;
                                  const disabled = category.id === main; // principal sempre incluída
                                  return (
                                    <DropdownMenuItem
                                      key={category.id}
                                      onSelect={(e) => {
                                        e.preventDefault();
                                        const current = list;
                                        let next: string[];
                                        if (checked) {
                                          if (disabled) return; // não remove a principal
                                          next = current.filter((id) => id !== category.id);
                                        } else {
                                          next = Array.from(new Set([...current, category.id]));
                                        }
                                        // garantir principal presente
                                        const ensured = main && !next.includes(main) ? [main, ...next] : next;
                                        setEditingVideo({ ...editingVideo, category_ids: ensured, categoryId: main || ensured[0] || '' });
                                      }}
                                    >
                                      <input type="checkbox" readOnly checked={checked} className="mr-2" />
                                      {category.name}
                                      {disabled && <span className="ml-auto text-xs text-muted-foreground">principal</span>}
                                    </DropdownMenuItem>
                                  );
                                })}
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <p className="text-xs text-muted-foreground mt-1">A categoria principal sempre ficará selecionada.</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-url">URL do Vídeo</Label>
                  <Input
                    id="edit-url"
                    type="url"
                    value={editingVideo.videoUrl}
                    onChange={(e) => setEditingVideo({ ...editingVideo, videoUrl: e.target.value })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-duration">Duração (segundos)</Label>
                  <Input
                    id="edit-duration"
                    type="number"
                    value={editingVideo.duration}
                    onChange={(e) => setEditingVideo({ ...editingVideo, duration: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="edit-release-at">Liberação programada</Label>
                  <Input
                    id="edit-release-at"
                    type="datetime-local"
                    value={toDateTimeLocalValue(editingVideo.release_at || editingVideo.releaseAt || null)}
                    onChange={(e) => setEditingVideo({ ...editingVideo, release_at: fromDateTimeLocalValue(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground">{AJUDA_LIBERACAO_CONTEUDO}</p>
                </div>
                  <div className="grid gap-2">
                    <Label>Módulo/Submódulo</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Buscar módulo..."
                      value={editModuleSearch}
                      onChange={(e) => setEditModuleSearch(e.target.value)}
                    />
                  </div>
                    <Select
                      value={editingVideo.module_id || editingVideo.moduleId || ''}
                      onValueChange={(value) => {
                        if (value === '__none__') {
                          // Remover módulo
                          setEditingVideo({ ...editingVideo, module_id: null, moduleId: null });
                        } else {
                          // Buscar a categoria do módulo selecionado
                          let moduleCategoryId = '';
                          for (const catId in modulesByCategory) {
                            const module = modulesByCategory[catId].find(m => m.id === value);
                            if (module) {
                              moduleCategoryId = module.categoryId || catId;
                              break;
                            }
                          }
                          
                          // Atualizar categoria do vídeo para corresponder à categoria do módulo
                          if (moduleCategoryId) {
                            const currentCategoryIds = editingVideo.category_ids || (editingVideo.categoryId ? [editingVideo.categoryId] : []);
                            const newCategoryIds = Array.from(new Set([moduleCategoryId, ...currentCategoryIds]));
                            setEditingVideo({
                              ...editingVideo,
                              module_id: value,
                              moduleId: value,
                              category_id: moduleCategoryId,
                              categoryId: moduleCategoryId,
                              category_ids: newCategoryIds,
                            });
                          } else {
                            setEditingVideo({ ...editingVideo, module_id: value, moduleId: value });
                          }
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um módulo (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                      {(() => {
                        // Coletar todos os módulos de todas as categorias
                        const allModules: Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }> = [];
                        for (const catId in modulesByCategory) {
                          allModules.push(...modulesByCategory[catId]);
                        }
                        
                        const filtered = allModules.filter((m) => 
                          editModuleSearch ? (m.title || '').toLowerCase().includes(editModuleSearch.toLowerCase()) : true
                        );
                        
                        // Agrupar por categoria
                        const byCategory: Record<string, Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }>> = {};
                        for (const m of filtered) {
                          const catId = m.categoryId || '';
                          if (!byCategory[catId]) byCategory[catId] = [];
                          byCategory[catId].push(m);
                        }
                        
                        const rootsByCategory: Record<string, Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }>> = {};
                        for (const catId in byCategory) {
                          rootsByCategory[catId] = byCategory[catId]
                            .filter((m) => !m.parentId)
                            .sort((a, b) => String(a.title).localeCompare(String(b.title)));
                        }
                        
                        // Função recursiva para renderizar módulos
                        const renderModuleOption = (module: { id: string; title: string; parentId?: string | null; categoryId?: string }, level: number, categoryModules: Array<{ id: string; title: string; parentId?: string | null; categoryId?: string }>): JSX.Element[] => {
                          const children = categoryModules
                            .filter(m => m.parentId === module.id)
                            .sort((a, b) => String(a.title).localeCompare(String(b.title)));
                          
                          const indent = '↳ '.repeat(level);
                          const padding = level * 1;
                          
                          const result: JSX.Element[] = [
                            <SelectItem key={module.id} value={module.id}>
                              <span style={{ paddingLeft: `${padding * 0.5}rem` }}>{indent}{module.title}</span>
                            </SelectItem>
                          ];

                          children.forEach(child => {
                            result.push(...renderModuleOption(child, level + 1, categoryModules));
                          });

                          return result;
                        };

                        return (
                          <div className="max-h-64 overflow-auto">
                            <SelectItem value="__none__">
                              <span className="text-muted-foreground italic">Sem módulo</span>
                            </SelectItem>
                            {Object.keys(rootsByCategory).map(catId => {
                              const category = categories.find(c => c.id === catId);
                              const roots = rootsByCategory[catId];
                              if (roots.length === 0) return null;
                              return (
                                <div key={catId}>
                                  <div className="px-2 py-1 text-xs text-muted-foreground uppercase border-t mt-1">
                                    {category?.name || 'Outros'}
                                  </div>
                                  {roots.map((root) => (
                                    <div key={root.id}>
                                      {renderModuleOption(root, 0, byCategory[catId])}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      </SelectContent>
                    </Select>
                  </div>
                <div className="grid gap-2">
                  <Label>Thumbnail</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(e, true)}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => editFileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Alterar Imagem
                    </Button>
                  </div>
                  {editingVideo.thumbnail && (
                    <img
                      src={editingVideo.thumbnail}
                      alt="Thumbnail preview"
                      className="mt-2 h-24 w-auto rounded-md object-cover"
                    />
                  )}
                </div>
                <div className="grid gap-2 rounded-md border p-3">
                  <Label className="text-sm font-medium">Este conteúdo possui formulário?</Label>
                  <p className="text-xs text-muted-foreground">
                    Marcando “Sim”, o aluno vê a chamada para a atividade ao terminar a aula.
                    É obrigatório informar um link ou anexar o formulário em Word ou PDF.
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm"
                            variant={editingVideo.has_form ? 'default' : 'outline'}
                            onClick={() => setEditingVideo({ ...editingVideo, has_form: true })}>Sim</Button>
                    <Button type="button" size="sm"
                            variant={!editingVideo.has_form ? 'default' : 'outline'}
                            onClick={() => setEditingVideo({ ...editingVideo, has_form: false, form_url: null, form_file: null })}>Não</Button>
                  </div>
                  {editingVideo.has_form && (
                    <div className="space-y-2 pt-1">
                      <Input
                        type="url"
                        inputMode="url"
                        placeholder="Link do formulário (Google Forms, Microsoft Forms...)"
                        value={editingVideo.form_url || ''}
                        onChange={(e) => setEditingVideo({ ...editingVideo, form_url: e.target.value })}
                      />
                      <div className="text-xs text-muted-foreground">ou anexe o arquivo:</div>
                      <Input
                        type="file"
                        accept="application/pdf,.doc,.docx"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const res = await uploadSupportFile(f);
                            setEditingVideo({ ...editingVideo, form_file: res });
                            toast({ title: 'Formulário anexado' });
                          } catch {
                            toast({ title: 'Falha ao enviar o formulário', variant: 'destructive' });
                          }
                        }}
                      />
                      {editingVideo.form_file && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="truncate">{editingVideo.form_file.filename}</span>
                          <button type="button" className="text-destructive"
                                  onClick={() => setEditingVideo({ ...editingVideo, form_file: null })}>remover</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label>Materiais de apoio</Label>
                  <Input
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,text/plain,text/csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (!files.length || !editingVideo) return;
                      try {
                        const uploaded = [];
                        for (const f of files) {
                          uploaded.push(await uploadSupportFile(f));
                        }
                        const current = editingVideo.support_files || editingVideo.supportFiles || [];
                        setEditingVideo({ ...editingVideo, support_files: [...current, ...uploaded] });
                      } catch {
                        toast({ title: 'Falha ao enviar material de apoio', variant: 'destructive' });
                      } finally {
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                  {((editingVideo.support_files || editingVideo.supportFiles || []).length > 0) && (
                    <div className="space-y-1">
                      {(editingVideo.support_files || editingVideo.supportFiles || []).map((f) => (
                        <div key={f.id} className="flex items-center justify-between rounded border px-2 py-1 text-xs">
                          <a href={f.url} target="_blank" rel="noreferrer" className="truncate underline">{f.filename}</a>
                          <button
                            type="button"
                            className="text-destructive"
                            onClick={() => {
                              const current = editingVideo.support_files || editingVideo.supportFiles || [];
                              setEditingVideo({ ...editingVideo, support_files: current.filter((x) => x.id !== f.id) });
                            }}
                          >
                            remover
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdateVideo}>Salvar Alterações</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={!!previewVideo} onOpenChange={(open) => !open && setPreviewVideo(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Pré-visualização: {previewVideo?.title}</DialogTitle>
              <DialogDescription>
                Incorporação do player do Vimeo
              </DialogDescription>
            </DialogHeader>
            {(() => {
              const pv = (previewVideo as unknown as { video_url?: string; videoUrl?: string } | null);
              const url = String(pv?.video_url || pv?.videoUrl || '');
              const lower = url.toLowerCase();
              const isPdf = lower.endsWith('.pdf');
              const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(lower);
              const isFile = (previewVideo?.content_type || previewVideo?.contentType) === 'file' || lower.includes('/api/files/');
              const vimeo = computeVimeoEmbed(previewVideo);
              return (
                <div className="relative aspect-video bg-black rounded-md overflow-hidden">
                  {isPdf ? (
                    <PdfViewer url={url} title={previewVideo?.title} className="absolute inset-0 w-full h-full" />
                  ) : isImg ? (
                    <img src={url} alt={previewVideo?.title || 'Imagem'} className="absolute inset-0 w-full h-full object-contain bg-black" />
                  ) : isFile ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted p-6 text-center">
                      <Upload className="h-10 w-10 text-primary" />
                      <p className="font-medium">Arquivo disponível para abertura externa</p>
                      <Button asChild>
                        <a href={url} target="_blank" rel="noreferrer">Abrir arquivo</a>
                      </Button>
                    </div>
                  ) : vimeo ? (
                    <iframe
                      src={vimeo as string}
                      className="absolute inset-0 w-full h-full"
                      frameBorder={0}
                      allow="autoplay; fullscreen; picture-in-picture"
                      title={previewVideo?.title || 'Vimeo player'}
                    />
                  ) : (
                    <div className="flex items-center justify-center text-muted-foreground h-full">
                      Sem pré-visualização disponível
                    </div>
                  )}
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewVideo(null)}>Fechar</Button>
              <Button onClick={() => navigate(`/video/${previewVideo?.id}`)}>Abrir página do vídeo</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
